import {
  dateToYearMonth,
  getMonthInputFormat,
  inputStringToYearMonth,
  validateYearMonth,
  yearMonthToInputString,
  type MonthInputValidationOptions,
  type YearMonth,
} from "@/common/helpers/date-input";
import { cn } from "@/common/helpers/utils";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { userConfigStore } from "@/store/user-config-store";
import { CalendarIcon } from "lucide-react";
import React, { useState, type InputHTMLAttributes } from "react";
import { useTranslation } from "react-i18next";

export interface MonthPickerInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "onBlur" | "type" | "value"
> {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  pastMonthsDisabled?: boolean;
  futureMonthsDisabled?: boolean;
  minMonth?: YearMonth;
  maxMonth?: YearMonth;
  ref?: React.Ref<HTMLInputElement>;
}

export function MonthPickerInput({
  value,
  onChange,
  onBlur,
  pastMonthsDisabled,
  futureMonthsDisabled,
  minMonth,
  maxMonth,
  className,
  disabled,
  onKeyDown,
  ref,
  ...inputProps
}: MonthPickerInputProps) {
  const { t, i18n } = useTranslation("common");
  const dateFormat = userConfigStore((state) => state.dateFormat);
  const selectedMonth = inputStringToYearMonth(value, { dateFormat });
  const currentMonth = dateToYearMonth();
  const [isOpen, setIsOpen] = useState(false);
  const [displayYear, setDisplayYear] = useState(selectedMonth?.year ?? currentMonth.year);
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const monthFormatter = new Intl.DateTimeFormat(locale, { month: "long" });
  const months = Array.from({ length: 12 }, (_, index) => monthFormatter.format(new Date(2020, index, 1)));
  const anchorYear = selectedMonth?.year ?? currentMonth.year;
  const years = Array.from({ length: 101 }, (_, index) => anchorYear - 50 + index);
  const validationOptions: MonthInputValidationOptions = {
    dateFormat,
    pastMonthsDisabled,
    futureMonthsDisabled,
    minMonth,
    maxMonth,
  };

  function isMonthDisabled(year: number, month: number): boolean {
    return validateYearMonth({ year, month }, validationOptions) !== undefined;
  }

  function isYearDisabled(year: number): boolean {
    return Array.from({ length: 12 }, (_, index) => index + 1).every((month) => isMonthDisabled(year, month));
  }

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen && selectedMonth) {
      setDisplayYear(selectedMonth.year);
    }
    setIsOpen(nextOpen);
  }

  function handleSelect(month: number) {
    if (isMonthDisabled(displayYear, month)) {
      return;
    }

    onChange(yearMonthToInputString({ year: displayYear, month }, { dateFormat }));
    onBlur?.();
    setIsOpen(false);
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const nextValue = event.target.value;
    onChange(nextValue);
    const parsedValue = inputStringToYearMonth(nextValue, { dateFormat });
    if (parsedValue) {
      setDisplayYear(parsedValue.year);
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (!disabled && event.key === "ArrowDown") {
      event.preventDefault();
      setIsOpen(true);
    }
    onKeyDown?.(event);
  }

  return (
    <div className={cn("relative", className)}>
      <Input
        {...inputProps}
        ref={ref}
        type="text"
        value={value}
        placeholder={getMonthInputFormat(dateFormat)}
        className="pr-9"
        disabled={disabled}
        onChange={handleInputChange}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
      />
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={t("SelectMonth")}
            disabled={disabled}
            className="text-muted-foreground hover:text-foreground absolute top-0 right-0 flex h-9 w-9 cursor-pointer items-center justify-center disabled:cursor-not-allowed disabled:opacity-50"
          >
            <CalendarIcon className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3" align="end" alignOffset={-8} sideOffset={10}>
          <Select value={String(displayYear)} onValueChange={(year) => setDisplayYear(Number(year))}>
            <SelectTrigger aria-label={t("SelectYear")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((year) => (
                <SelectItem key={year} value={String(year)} disabled={isYearDisabled(year)}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="mt-3 grid grid-cols-3 gap-1">
            {months.map((month, index) => {
              const monthNumber = index + 1;
              const isSelected = selectedMonth?.year === displayYear && selectedMonth.month === monthNumber;
              const isDisabled = isMonthDisabled(displayYear, monthNumber);

              return (
                <button
                  key={month}
                  type="button"
                  aria-pressed={isSelected}
                  disabled={isDisabled}
                  className={cn(
                    "hover:bg-accent hover:text-accent-foreground h-9 rounded-md px-2 text-sm capitalize disabled:pointer-events-none disabled:opacity-40",
                    isSelected &&
                      "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground",
                  )}
                  onClick={() => handleSelect(monthNumber)}
                >
                  {month}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
